import CallService from "@/lib/calls"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { NavArrowDown, Clock } from "iconoir-react"
import moment from "moment"
import React, { useCallback, useContext, useState } from "react"
import { Link } from "react-router-dom"
import { VibratoClientContext, NotificationsContext } from "@/components/dashboard/contexts"

interface RetryInfoCardProps {
  call: any
  onUpdate: (updatedCall: any) => void
}

export default function RetryInfoCard({ call, onUpdate }: RetryInfoCardProps): React.JSX.Element | null {
  if (!call.retry_schedule) {
    return null
  }

  const callRetries = call.retry_schedule.call_retries
  const vibratoClient = useContext(VibratoClientContext)
  const notifications = useContext(NotificationsContext)
  const [isCancellingRetries, setIsCancellingRetries] = useState(false)

  const handleCancelRetries = useCallback(async () => {
    console.log("Canceling retries...")
    setIsCancellingRetries(true)

    if (vibratoClient != null && call.retry_schedule) {
      try {
        let lastRetryCall = call.retry_schedule.call_retries[call.retry_schedule.call_retries.length - 1]
        if (!lastRetryCall) {
          lastRetryCall = call
        }

        await vibratoClient.endCall(lastRetryCall.id)

        let updatedCall = call
        while (!updatedCall.retry_schedule.completed) {
          await new Promise((resolve) => setTimeout(resolve, 2000))
          updatedCall = await vibratoClient.getCall(call.id)
        }

        onUpdate(updatedCall)

        notifications({
          type: "success",
          header: "Success",
          id: "cancel-retries-success",
          message: "Retries have been canceled."
        })
      } catch (e) {
        console.log("Error canceling retries: ", e)
        notifications({
          type: "error",
          header: "Error",
          id: "cancel-retries-error",
          message: "Failed to cancel retries. Please try again."
        })
      } finally {
        setIsCancellingRetries(false)
      }
    }
  }, [vibratoClient, call, onUpdate, notifications])

  return (
    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
      <Disclosure defaultOpen={true}>
        {({ open }) => (
          <div>
            <DisclosureButton
              id="retry-info-disclosure-button"
              className="flex w-full justify-between items-center p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-base font-medium text-gray-900">Retry Information</h3>
              </div>
              <NavArrowDown
                className={`${
                  open ? "rotate-180 transform" : ""
                } h-5 w-5 text-blue-500 transition-transform duration-200`}
              />
            </DisclosureButton>
            <DisclosurePanel className="border-t border-gray-200 p-5">
              <p className="text-sm text-gray-600 mb-4">Vibrato will automatically retry the call if it fails.</p>

              <div className="mb-4">
                <div>
                  <p className="text-sm text-blue-600 font-medium mb-1">Retry Status</p>
                  {!call.retry_schedule.completed ? (
                    <p className="flex items-center text-gray-800">
                      Retries in progress
                      <span className="ml-2 animate-pulse text-blue-500">●</span>
                    </p>
                  ) : call.retry_schedule.succeeded ? (
                    <p className="text-green-600 font-medium">The call succeeded via retry.</p>
                  ) : (
                    <p className="text-red-600 font-medium">All retries failed.</p>
                  )}
                </div>
              </div>

              <div className="w-full grid grid-cols-2 gap-x-2 gap-y-4 justify-between my-2">
                <div className="flex flex-col">
                  <p className="text-sm text-blue-600 font-medium mb-1">Retries Left</p>
                  <p className="text-gray-800">
                    {call.retry_schedule.max_retries - call.retry_schedule.retries} of {call.retry_schedule.max_retries}
                  </p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm text-blue-600 font-medium mb-1">Delay Between Retries</p>
                  <p className="text-gray-800">{call.retry_schedule.delay_between_retries_seconds / 60} minutes</p>
                </div>
              </div>
              {call.retry_schedule.next_retry_at && !call.retry_schedule.completed && (
                <p className="text-gray-800 mt-4">
                  Next retry: {moment(call.retry_schedule.next_retry_at).format("MMM D, YYYY h:mm A")}
                </p>
              )}
              {callRetries.length > 0 && (
                <>
                  <hr className="my-4 border-gray-200" />
                  <h3 className="text-sm font-medium mb-3 text-blue-600">Call Attempts</h3>

                  {callRetries.map((retryCall: any, index: number) => (
                    <div key={retryCall.id} className="mb-3 last:mb-0">
                      <h3 className="">
                        <Link
                          to={`/calls/${retryCall.id}`}
                          className="text-gray-600 hover:text-blue-600 transition-colors text-sm cursor-pointer"
                        >
                          Retry {index + 1}
                          {retryCall.id === call.id ? " (this call)" : ""}
                        </Link>
                        {retryCall.status === "in_progress" && (
                          <span className="ml-2 animate-pulse text-blue-500">●</span>
                        )}
                      </h3>
                      {retryCall.summary ? (
                        <p className={`mt-1 ${!retryCall.succeeded ? "text-red-600" : "text-green-600"}`}>
                          {retryCall.summary}
                        </p>
                      ) : (
                        <p className="mt-1 text-gray-500">{new CallService(retryCall).getStatusForDisplay()}</p>
                      )}
                    </div>
                  ))}
                </>
              )}

              {!call.retry_schedule.completed && (
                <div className="mt-6 flex">
                  <button
                    onClick={handleCancelRetries}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow-md transition-all disabled:bg-red-400"
                    disabled={isCancellingRetries}
                  >
                    {isCancellingRetries ? "Cancelling..." : "Cancel Retries"}
                  </button>
                </div>
              )}
            </DisclosurePanel>
          </div>
        )}
      </Disclosure>
    </div>
  )
}
